import { API_BASE_URL } from "./config";

/** Error thrown for any non-2xx API response, carrying a friendly message and optional field errors. */
export class ApiError extends Error {
	status: number;
	fieldErrors?: Record<string, string[]>;

	constructor(message: string, status: number, fieldErrors?: Record<string, string[]>) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.fieldErrors = fieldErrors;
	}
}

type RequestOptions = {
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	body?: unknown;
	token?: string | null;
};

/** Shape of the backend's success envelope (CommonApiResult). */
type ApiEnvelope<T> = {
	status?: string;
	succeeded?: boolean;
	message?: string;
	data?: T;
};

const extractErrorMessage = (payload: unknown, status: number): ApiError => {
	if (payload && typeof payload === "object") {
		const body = payload as Record<string, unknown>;

		// FluentValidation / ModelState errors: { errors: { field: [msg] }, title }
		const errors = body.errors as Record<string, string[]> | undefined;
		if (errors && typeof errors === "object") {
			const first = Object.values(errors).flat()[0];

			return new ApiError(first ?? (body.title as string) ?? "Validation failed.", status, errors);
		}

		const message = (body.detail as string) ?? (body.title as string) ?? (body.message as string);
		if (message) {
			return new ApiError(message, status);
		}
	}

	return new ApiError(
		status === 401 ? "Your session has expired. Please sign in again." : "Something went wrong. Please try again.",
		status
	);
};

export const apiRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
	const { method = "GET", body, token } = options;

	// Let the browser set the multipart boundary for FormData; only JSON bodies get an explicit Content-Type.
	const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

	const headers: Record<string, string> = { Accept: "application/json" };
	if (body !== undefined && !isFormData) {
		headers["Content-Type"] = "application/json";
	}
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	let requestBody: BodyInit | undefined;
	if (body !== undefined) {
		requestBody = isFormData ? (body as FormData) : JSON.stringify(body);
	}

	let response: Response;
	try {
		response = await fetch(`${API_BASE_URL}/api/v1${path}`, {
			method,
			headers,
			// Send the httpOnly refresh cookie on auth calls (ignored by endpoints that don't read it).
			credentials: "include",
			body: requestBody
		});
	} catch {
		throw new ApiError("Unable to reach the server. Check your connection and try again.", 0);
	}

	const text = await response.text();
	const payload = text ? JSON.parse(text) : undefined;

	if (!response.ok) {
		throw extractErrorMessage(payload, response.status);
	}

	return (payload as ApiEnvelope<T>)?.data as T;
};
